/**
 * Admin — Case-type prefixes master data.
 *
 * Super admin curates the platform-wide list of case-type abbreviations
 * (OS, CC, WP(C), CRMA, …) used when composing the Original Case Number.
 *
 * - Add new (e.g., state-specific codes)
 * - Edit label / category / sort order / enabled
 * - Disable (toggle) — system prefixes can't be deleted, only disabled
 * - Delete (only user-added, never system)
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Hash, Plus, Search, X, Loader2, Edit3, Trash2, ToggleLeft, ToggleRight, ShieldCheck } from "lucide-react";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const EMPTY_DRAFT = { code: "", label: "", category: "misc", enabled: true, sort_order: 999 };

export default function AdminCaseTypePrefixes() {
    const [rows, setRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [showDisabled, setShowDisabled] = useState(true);

    const [draft, setDraft] = useState(null);   // { mode:'create'|'edit', row }
    const [saving, setSaving] = useState(false);
    const [busyCode, setBusyCode] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/case-type-prefixes");
            setRows(data.prefixes || []);
            setCategories(data.categories || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return rows.filter(r => {
            if (filterCategory && r.category !== filterCategory) return false;
            if (!showDisabled && !r.enabled) return false;
            if (needle) {
                const blob = `${r.code} ${r.label} ${r.category}`.toLowerCase();
                if (!blob.includes(needle)) return false;
            }
            return true;
        });
    }, [rows, q, filterCategory, showDisabled]);

    const grouped = useMemo(() => {
        const g = {};
        for (const r of filtered) {
            (g[r.category] = g[r.category] || []).push(r);
        }
        return g;
    }, [filtered]);

    const openCreate = () => setDraft({ mode: "create", row: { ...EMPTY_DRAFT, category: categories[0] || "misc" } });
    const openEdit = (row) => setDraft({ mode: "edit", row: { ...row } });
    const closeDraft = () => setDraft(null);

    const save = async () => {
        if (!draft) return;
        const r = draft.row;
        if (!r.code?.trim() || !r.label?.trim()) {
            toast.error("Code and label are required");
            return;
        }
        setSaving(true);
        try {
            if (draft.mode === "create") {
                await api.post("/admin/case-type-prefixes", {
                    code: r.code.trim(),
                    label: r.label.trim(),
                    category: r.category,
                    enabled: r.enabled,
                    sort_order: Number(r.sort_order) || 999,
                });
                toast.success(`Prefix '${r.code.trim()}' added`);
            } else {
                await api.patch(`/admin/case-type-prefixes/${encodeURIComponent(r.code)}`, {
                    label: r.label.trim(),
                    category: r.category,
                    enabled: r.enabled,
                    sort_order: Number(r.sort_order) || 999,
                });
                toast.success(`Prefix '${r.code}' updated`);
            }
            closeDraft();
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const toggleEnabled = async (row) => {
        setBusyCode(row.code);
        try {
            await api.patch(`/admin/case-type-prefixes/${encodeURIComponent(row.code)}`, { enabled: !row.enabled });
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusyCode(""); }
    };

    const remove = async (row) => {
        if (row.is_system) { toast.error("System prefixes can't be deleted — disable instead"); return; }
        if (!window.confirm(`Delete prefix '${row.code}'? This can't be undone.`)) return;
        setBusyCode(row.code);
        try {
            await api.delete(`/admin/case-type-prefixes/${encodeURIComponent(row.code)}`);
            toast.success(`Prefix '${row.code}' deleted`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusyCode(""); }
    };

    return (
        <div className="space-y-6" data-testid="admin-case-types-page">
            <header className="border border-border bg-card px-6 py-5 rounded-sm">
                <div className="flex items-center gap-2 text-primary text-[10px] uppercase tracking-[0.22em] mb-2">
                    <Hash className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Master data
                </div>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                    Case-type prefixes
                </h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Configure the abbreviations that go in front of every Original Case Number
                    (e.g. <span className="text-primary font-mono">OS</span> 253/2014,
                    <span className="text-primary font-mono"> WP(C)</span> 5421/2024).
                    Every tenant uses this list for autocomplete and validation.
                </p>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-primary/30 text-primary text-[10px] uppercase tracking-[0.18em] rounded-sm bg-primary/[0.04] mt-4">
                    <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
                    Platform-wide · Audit-logged
                </div>
            </header>

            {/* ---- toolbar ---- */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,auto,auto] gap-2 items-center">
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5}/>
                    <input
                        type="text"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Search code · label · category"
                        data-testid="case-types-search"
                        className="w-full bg-background border border-border focus:border-primary outline-none pl-8 pr-8 py-2 text-xs text-foreground placeholder:text-muted-foreground"
                    />
                    {q && <button onClick={() => setQ("")} aria-label="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5"/></button>}
                </div>
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} data-testid="case-types-filter-category"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground">
                    <option value="">All categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)}
                        data-testid="case-types-show-disabled" className="accent-primary"/>
                    Include disabled
                </label>
                <button onClick={openCreate} data-testid="case-types-new"
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.18em] whitespace-nowrap">
                    <Plus className="w-3 h-3" strokeWidth={1.5}/> New prefix
                </button>
            </div>

            <div className="text-xs text-muted-foreground" data-testid="case-types-count">
                {loading ? "Loading…" : `${filtered.length} of ${rows.length} prefixes`}
            </div>

            {/* ---- grouped table ---- */}
            <div className="space-y-6">
                {Object.keys(grouped).sort().map(cat => (
                    <CategoryGroupTable key={cat} cat={cat} rows={grouped[cat]} busyCode={busyCode}
                        toggleEnabled={toggleEnabled} openEdit={openEdit} remove={remove}/>
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="border border-border bg-card p-8 text-center text-xs text-muted-foreground">
                        No prefixes match the current filters.
                    </div>
                )}
            </div>

            {/* ---- create / edit modal ---- */}
            {draft && (
                <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={closeDraft}>
                    <div className="bg-card border border-border w-full max-w-md p-5"
                         onClick={e => e.stopPropagation()} data-testid="case-type-modal">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-primary mb-2">
                            {draft.mode === "create" ? "New prefix" : "Edit prefix"}
                        </div>
                        <h3 className="text-lg text-foreground mb-4">{draft.mode === "create" ? "Add a case-type code" : `Edit ${draft.row.code}`}</h3>

                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Code</label>
                                <input type="text" value={draft.row.code}
                                    onChange={e => setDraft({ ...draft, row: { ...draft.row, code: e.target.value }})}
                                    disabled={draft.mode === "edit"}
                                    data-testid="case-type-modal-code"
                                    placeholder="e.g. OS · WP(C) · Crl.M.C."
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground font-mono disabled:opacity-60"/>
                                {draft.mode === "edit" && <div className="text-[10px] text-muted-foreground mt-1">Code cannot be changed — delete and recreate if needed</div>}
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Label</label>
                                <input type="text" value={draft.row.label}
                                    onChange={e => setDraft({ ...draft, row: { ...draft.row, label: e.target.value }})}
                                    data-testid="case-type-modal-label"
                                    placeholder="e.g. Original Suit"
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Category</label>
                                    <select value={draft.row.category}
                                        onChange={e => setDraft({ ...draft, row: { ...draft.row, category: e.target.value }})}
                                        data-testid="case-type-modal-category"
                                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground">
                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground block mb-1.5">Sort order</label>
                                    <input type="number" value={draft.row.sort_order}
                                        onChange={e => setDraft({ ...draft, row: { ...draft.row, sort_order: e.target.value }})}
                                        data-testid="case-type-modal-sort"
                                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground font-mono"/>
                                </div>
                            </div>
                            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                                <input type="checkbox" checked={draft.row.enabled}
                                    onChange={e => setDraft({ ...draft, row: { ...draft.row, enabled: e.target.checked }})}
                                    data-testid="case-type-modal-enabled" className="accent-primary"/>
                                Enabled (visible to tenants for autocomplete)
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={closeDraft}
                                className="px-3 py-1.5 border border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
                                Cancel
                            </button>
                            <button onClick={save} disabled={saving}
                                data-testid="case-type-modal-save"
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary text-primary-foreground hover:bg-primary text-[10px] uppercase tracking-[0.16em] font-medium disabled:opacity-50">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : null}
                                {draft.mode === "create" ? "Create" : "Save changes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CategoryGroupTable({ cat, rows, busyCode, toggleEnabled, openEdit, remove }) {
    const ctrl = useTableControls(rows, {
        accessors: {
            status: (r) => r.enabled ? "enabled" : "disabled",
            source: (r) => r.is_system ? "system" : "custom",
        },
    });
    return (
        <div className="border border-border bg-card">
            <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-[0.22em] text-primary flex items-center gap-2">
                <span>{cat} · {rows.length}</span>
                <FilterChip ctrl={ctrl} className="ml-auto"/>
            </div>
            <table className="w-full text-xs">
                <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                    <tr>
                        <SortableTh field="code" ctrl={ctrl} className="text-left p-2 w-32">Code</SortableTh>
                        <SortableTh field="label" ctrl={ctrl} className="text-left p-2">Label</SortableTh>
                        <SortableTh field="sort_order" ctrl={ctrl} className="text-left p-2 w-20">Order</SortableTh>
                        <SortableTh field="status" ctrl={ctrl} className="text-left p-2 w-24">Status</SortableTh>
                        <SortableTh field="source" ctrl={ctrl} className="text-left p-2 w-24">Source</SortableTh>
                        <th className="text-right p-2 w-32 uppercase tracking-[0.16em]">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {ctrl.rows.map(r => {
                        const busy = busyCode === r.code;
                        return (
                            <tr key={r.code} className={`border-t border-border ${r.enabled ? "" : "opacity-50"}`} data-testid={`case-type-row-${r.code}`}>
                                <td className="p-2 font-mono text-primary">{r.code}</td>
                                <td className="p-2">{r.label}</td>
                                <td className="p-2 text-muted-foreground font-mono">{r.sort_order}</td>
                                <td className="p-2">
                                    <button onClick={() => toggleEnabled(r)} disabled={busy} data-testid={`case-type-toggle-${r.code}`}
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] hover:text-primary">
                                        {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : (r.enabled ? <ToggleRight className="w-3.5 h-3.5 text-primary"/> : <ToggleLeft className="w-3.5 h-3.5 text-muted-foreground"/>)}
                                        {r.enabled ? "Enabled" : "Disabled"}
                                    </button>
                                </td>
                                <td className="p-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                    {r.is_system ? "system" : "custom"}
                                </td>
                                <td className="p-2 text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <button onClick={() => openEdit(r)} title="Edit" data-testid={`case-type-edit-${r.code}`}
                                            className="p-1.5 text-muted-foreground hover:text-primary hover:bg-background">
                                            <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                        </button>
                                        <button onClick={() => remove(r)} title={r.is_system ? "System prefix — disable instead" : "Delete"}
                                            disabled={r.is_system || busy}
                                            data-testid={`case-type-delete-${r.code}`}
                                            className="p-1.5 text-muted-foreground hover:text-[#A6181B] hover:bg-background disabled:opacity-20 disabled:hover:text-muted-foreground">
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

